import { Link } from "react-router-dom";
import { useAgingReport } from "@/api/payments";

const currency = new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 });

export default function CarteraPage() {
  const { data, isLoading } = useAgingReport();

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Cartera</h1>
        <p className="text-sm text-gray-500">Saldo pendiente por cliente, agrupado por antigüedad de vencimiento.</p>
      </div>

      {data && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <SummaryCard label="Al día" value={data.totals.current} />
          <SummaryCard label="1-30 días" value={data.totals.days1To30} tone="amber" />
          <SummaryCard label="31-60 días" value={data.totals.days31To60} tone="amber" />
          <SummaryCard label="61-90 días" value={data.totals.days61To90} tone="red" />
          <SummaryCard label="90+ días" value={data.totals.daysOver90} tone="red" />
          <SummaryCard label="Total cartera" value={data.totals.totalOutstanding} tone="brand" />
        </div>
      )}

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Al día</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">1-30</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">31-60</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">61-90</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">90+</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Total</th>
              <th className="px-4 py-3 text-right font-medium text-gray-500">Facturas</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && (!data || data.rows.length === 0) && (
              <tr>
                <td colSpan={8} className="px-4 py-10 text-center text-gray-400">
                  No hay saldos pendientes. Toda la cartera está al día.
                </td>
              </tr>
            )}
            {data?.rows.map((row) => (
              <tr key={row.clientId} className="hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">
                  <Link to={`/clients/${row.clientId}`} className="hover:text-brand-600">
                    {row.clientName}
                  </Link>
                </td>
                <td className="px-4 py-3 text-right text-gray-600">{currency.format(row.current)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{currency.format(row.days1To30)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{currency.format(row.days31To60)}</td>
                <td className="px-4 py-3 text-right text-red-600">{currency.format(row.days61To90)}</td>
                <td className="px-4 py-3 text-right text-red-600">{currency.format(row.daysOver90)}</td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900">{currency.format(row.totalOutstanding)}</td>
                <td className="px-4 py-3 text-right text-gray-500">{row.invoiceCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone = "gray" }: { label: string; value: number; tone?: "gray" | "amber" | "red" | "brand" }) {
  const toneStyle: Record<string, string> = {
    gray: "text-gray-900",
    amber: "text-amber-600",
    red: "text-red-600",
    brand: "text-brand-600",
  };
  return (
    <div className="card p-4">
      <p className="text-xs text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${toneStyle[tone]}`}>{currency.format(value)}</p>
    </div>
  );
}
